/**
 * Forge Coach SSE client. POST /api/v1/coach/message streams text/event-stream;
 * because it's a POST (not GET) we use fetch + ReadableStream, not EventSource.
 * Non-streaming GETs live in client.ts (they reuse the axios refresh interceptor).
 */
import { API_BASE, getAccessToken } from "./client";

// labelKey is an i18n key (coach namespace), not display text — resolved
// with t() at render (see PhaseIndicator.tsx). Found as a genuine gap during
// the i18n Phase 5 spot-review: this is a plain .ts file, so the Phase 2
// ratchet (which only scans .tsx) never saw these 8 hardcoded English
// labels, even though they render to the user via PhaseIndicator.
export const PHASES: { key: string; labelKey: string }[] = [
  { key: "intake", labelKey: "PhaseIndicator.phase.intake" },
  { key: "role_selection", labelKey: "PhaseIndicator.phase.roleSelection" },
  { key: "constraints", labelKey: "PhaseIndicator.phase.constraints" },
  { key: "market", labelKey: "PhaseIndicator.phase.market" },
  { key: "gap_analysis", labelKey: "PhaseIndicator.phase.gapAnalysis" },
  { key: "action_plan", labelKey: "PhaseIndicator.phase.actionPlan" },
  { key: "interview_prep", labelKey: "PhaseIndicator.phase.interviewPrep" },
  { key: "monthly_review", labelKey: "PhaseIndicator.phase.monthlyReview" },
];

export type CoachStreamEvent =
  | { type: "message_start"; model: string }
  | { type: "content_delta"; text: string }
  | { type: "dossier_update"; path: string; value: Record<string, any> }
  | { type: "phase_change"; from: string; to: string }
  | { type: "message_complete"; tokens_input: number; tokens_output: number }
  | { type: "error"; message: string };

/** Thrown on 402 (monthly cap) or 429 (24h window) — returned before the stream. */
export class CoachRateLimitError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "CoachRateLimitError";
    this.status = status;
  }
}

function parseSSEBlock(raw: string): CoachStreamEvent | null {
  let event = "";
  let data = "";
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!event) return null;
  let p: any = {};
  try {
    p = data ? JSON.parse(data) : {};
  } catch {
    return null;
  }
  switch (event) {
    case "message_start":
      return { type: "message_start", model: p.model };
    case "content_delta":
      return { type: "content_delta", text: p.text ?? "" };
    case "dossier_update":
      return { type: "dossier_update", path: p.path, value: p.value };
    case "phase_change":
      return { type: "phase_change", from: p.from, to: p.to };
    case "message_complete":
      return {
        type: "message_complete",
        tokens_input: p.tokens_input ?? 0,
        tokens_output: p.tokens_output ?? 0,
      };
    case "error":
      return { type: "error", message: p.message ?? "The coach hit an error." };
    default:
      return null;
  }
}

export async function streamCoachMessage(
  message: string,
  onEvent: (ev: CoachStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const resp = await fetch(`${API_BASE}/api/v1/coach/message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAccessToken() ?? ""}`,
    },
    body: JSON.stringify({ message }),
    signal,
  });

  if (resp.status === 402 || resp.status === 429) {
    const body = await resp.json().catch(() => ({} as any));
    throw new CoachRateLimitError(resp.status, body?.detail ?? "Coach limit reached.");
  }
  if (!resp.ok || !resp.body) {
    throw new Error(`Coach request failed (${resp.status}).`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const ev = parseSSEBlock(block);
      if (ev) onEvent(ev);
    }
  }
}
